import React, { useState, useEffect } from 'react';
import {
    FileText,
    TrendingUp,
    DollarSign,
    Calendar,
    Filter,
    Download,
    RefreshCw,
    Eye,
    Clock,
    CheckCircle,
    AlertCircle,
    User,
    Building,
    Receipt,
    CreditCard,
    Brain,
    RotateCcw,
    ShoppingBag,
    Search,
    Package,
    ExternalLink
} from 'lucide-react';

const EmployeeDashboard = ({ user, userData, currentOrganization }) => {
    // Use userData if user is not provided (for backward compatibility)
    const currentUser = user || userData;

    console.log('EmployeeDashboard received props:', { user, userData, currentOrganization });

    // Early return if user data is not available
    if (!currentUser) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                <span className="ml-2">Loading user data...</span>
            </div>
        );
    }

    // Additional check for user.companyId specifically
    if (!currentUser.companyId) {
        console.log('User object exists but companyId is missing:', currentUser);
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                <span className="ml-2">Loading company information...</span>
            </div>
        );
    }

    const [activeTab, setActiveTab] = useState('daily-spends');
    const [mySubmissions, setMySubmissions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [stats, setStats] = useState({
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        totalAmount: 0
    });

    // Modal states
    const [selectedItem, setSelectedItem] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [itemDetails, setItemDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.zapbooksai.subraps.com/api';

    // Navigation tabs for employee - same structure as approver
    const employeeTabs = [
        {
            id: 'daily-spends',
            name: 'Daily Spend',
            icon: Brain,
            type: 'daily_spend'
        },
        {
            id: 'expenses',
            name: 'Expenses',
            icon: Receipt,
            type: 'expense'
        },
        {
            id: 'reimbursements',
            name: 'Reimbursements',
            icon: FileText,
            type: 'reimbursement'
        },
        {
            id: 'purchases',
            name: 'Purchases',
            icon: ShoppingBag,
            type: 'purchase'
        },
        {
            id: 'vendor-credits',
            name: 'Debit Notes',
            icon: CreditCard,
            type: 'debit_note'
        },
        {
            id: 'recurring-expenses',
            name: 'Recurring Expenses',
            icon: RotateCcw,
            type: 'recurring_expense'
        },
        {
            id: 'sales-data',
            name: 'Sales/Invoices',
            icon: TrendingUp,
            type: 'sales_data'
        },
        {
            id: 'credit-notes',
            name: 'Credit Notes',
            icon: FileText,
            type: 'credit_note'
        },
        {
            id: 'recurring-invoices',
            name: 'Recurring Invoices',
            icon: RotateCcw,
            type: 'recurring_invoice'
        }
    ];

    useEffect(() => {
        fetchMySubmissions();
    }, [activeTab, currentUser.companyId]);

    useEffect(() => {
        fetchEmployeeStats();
    }, [currentUser.companyId]);

    const fetchEmployeeStats = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/employee/stats?company_id=${currentUser.companyId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setStats(data.stats);
                }
            }
        } catch (error) {
            console.error('Error fetching employee stats:', error);
        }
    };

    const fetchMySubmissions = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const currentTabConfig = employeeTabs.find(tab => tab.id === activeTab);

            if (!currentTabConfig) {
                setMySubmissions([]);
                setLoading(false);
                return;
            }

            const queryParams = new URLSearchParams({
                company_id: currentUser.companyId,
                filter: currentTabConfig.type,
                dateRange: 'all'
            });

            const response = await fetch(`${API_BASE_URL}/employee/my-submissions?${queryParams}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setMySubmissions(data.data.submissions || []);
                } else {
                    console.error('API returned error:', data.message);
                    setMySubmissions([]);
                }
            } else {
                console.error('Failed to fetch submissions:', response.status);
                setMySubmissions([]);
                if (response.status === 403) {
                    alert('Access denied. Please check your permissions.');
                }
            }
        } catch (error) {
            console.error('Error fetching submissions:', error);
            setMySubmissions([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchItemDetails = async (item) => {
        setLoadingDetails(true);
        try {
            const token = localStorage.getItem('authToken');
            const currentTabConfig = employeeTabs.find(tab => tab.id === activeTab);

            const response = await fetch(`${API_BASE_URL}/employee/item-details?itemId=${item.id}&itemType=${currentTabConfig.type}&company_id=${currentUser.companyId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setItemDetails(data.data);
                    setSelectedItem(item);
                    setShowDetailsModal(true);
                }
            }
        } catch (error) {
            console.error('Error fetching item details:', error);
            alert('Failed to fetch item details');
        } finally {
            setLoadingDetails(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending':
            case 'pending_approval':
                return 'text-yellow-600 bg-yellow-100';
            case 'approved':
                return 'text-green-600 bg-green-100';
            case 'rejected':
                return 'text-red-600 bg-red-100';
            default:
                return 'text-gray-600 bg-gray-100';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'pending':
            case 'pending_approval':
                return <Clock className="w-4 h-4" />;
            case 'approved':
                return <CheckCircle className="w-4 h-4" />;
            case 'rejected':
                return <AlertCircle className="w-4 h-4" />;
            default:
                return <FileText className="w-4 h-4" />;
        }
    };

    const getAccountingStatusColor = (status) => {
        return status === 'Accounted'
            ? 'text-green-600 bg-green-100'
            : 'text-orange-600 bg-orange-100';
    };

    const formatCurrency = (amount) => {
        if (!amount) return '₹0';
        return `₹${parseFloat(amount).toLocaleString('en-IN')}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN');
    };

    const exportData = () => {
        if (mySubmissions.length === 0) {
            alert('No data to export');
            return;
        }

        const csvHeaders = [
            'Type',
            'Reference Number',
            'Description',
            'Amount',
            'Date',
            'Vendor/Customer',
            'Status',
            'Accounting Status',
            'Approved By',
            'Approved Date',
            'Created Date'
        ];

        const csvData = mySubmissions.map(item => [
            item.type || 'Unknown',
            item.reference_number || item.id,
            item.description || '',
            item.amount || 0,
            formatDate(item.date),
            item.vendor_name || item.customer_name || '',
            item.status === 'pending_approval' ? 'Pending' : item.status,
            item.accounting_status,
            item.approved_by || '',
            formatDate(item.approved_date),
            formatDate(item.created_at)
        ]);

        const csvContent = [csvHeaders, ...csvData]
            .map(row => row.map(cell => `"${cell}"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `my-${activeTab}-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const filteredData = mySubmissions.filter(item => {
        const matchesSearch = searchTerm === '' ||
            item.reference_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());

        const itemStatus = item.status || 'pending';
        const matchesFilter = filter === 'all' ||
            (filter === 'pending' && (itemStatus === 'pending' || itemStatus === 'pending_approval')) ||
            (filter === 'approved' && itemStatus === 'approved') ||
            (filter === 'rejected' && itemStatus === 'rejected');

        return matchesSearch && matchesFilter;
    });

    // Item Details Modal
    const ItemDetailsModal = () => {
        if (!showDetailsModal || !itemDetails) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg max-w-2xl w-full m-4 max-h-[90vh] overflow-y-auto">
                    <div className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900">My Submission Details</h2>
                            <button
                                onClick={() => setShowDetailsModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="space-y-6">
                            {/* Item Information */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-3">Submission Information</h3>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-gray-600">ID:</span>
                                        <span className="ml-2 font-medium">#{itemDetails.id}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Amount:</span>
                                        <span className="ml-2 font-medium">{formatCurrency(itemDetails.amount || itemDetails.total)}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Date:</span>
                                        <span className="ml-2 font-medium">{formatDate(itemDetails.created_at || itemDetails.date)}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Status:</span>
                                        <span className={`ml-2 px-2 py-1 text-xs rounded-full ${getStatusColor(itemDetails.approver_status || 'pending_approval')}`}>
                                            {itemDetails.approver_status === 'pending_approval' ? 'Pending' : itemDetails.approver_status || 'Pending'}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Accounting Status:</span>
                                        <span className={`ml-2 px-2 py-1 text-xs rounded-full ${getAccountingStatusColor(itemDetails.accounting_status || 'Not Accounted')}`}>
                                            {itemDetails.accounting_status || 'Not Accounted'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Invoice Link Section */}
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-3">Invoice Document</h3>
                                {itemDetails.aws_link ? (
                                    <div className="flex items-center gap-2">
                                        <ExternalLink className="w-4 h-4 text-blue-600" />
                                        <a
                                            href={itemDetails.aws_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:text-blue-800 font-medium underline"
                                        >
                                            View Invoice
                                        </a>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 text-gray-500">
                                        <FileText className="w-4 h-4" />
                                        <span>No Invoice Available</span>
                                    </div>
                                )}
                            </div>

                            {/* Description/Details */}
                            {(itemDetails.description || itemDetails.nature_of_expenses || itemDetails.items) && (
                                <div className="bg-purple-50 p-4 rounded-lg">
                                    <h3 className="font-semibold text-gray-900 mb-3">Description</h3>
                                    <p className="text-sm text-gray-700">
                                        {itemDetails.description || itemDetails.nature_of_expenses || itemDetails.items || 'No description available'}
                                    </p>
                                </div>
                            )}

                            {/* Vendor/Customer Information */}
                            {(itemDetails.vendor_name || itemDetails.customer_name) && (
                                <div className="bg-orange-50 p-4 rounded-lg">
                                    <h3 className="font-semibold text-gray-900 mb-3">
                                        {itemDetails.vendor_name ? 'Vendor' : 'Customer'} Information
                                    </h3>
                                    <div className="text-sm">
                                        <span className="text-gray-600">Name:</span>
                                        <span className="ml-2 font-medium">{itemDetails.vendor_name || itemDetails.customer_name}</span>
                                    </div>
                                </div>
                            )}

                            {/* Approval Information */}
                            {itemDetails.approver_status && itemDetails.approver_status !== 'pending_approval' && (
                                <div className="bg-yellow-50 p-4 rounded-lg">
                                    <h3 className="font-semibold text-gray-900 mb-3">Approval Information</h3>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        {itemDetails.approved_by && (
                                            <div>
                                                <span className="text-gray-600">Processed By:</span>
                                                <span className="ml-2 font-medium">{itemDetails.approved_by}</span>
                                            </div>
                                        )}
                                        {itemDetails.approved_date && (
                                            <div>
                                                <span className="text-gray-600">Processed Date:</span>
                                                <span className="ml-2 font-medium">{formatDate(itemDetails.approved_date)}</span>
                                            </div>
                                        )}
                                        {itemDetails.rejection_reason && (
                                            <div className="col-span-2">
                                                <span className="text-gray-600">Rejection Reason:</span>
                                                <span className="ml-2 font-medium text-red-600">{itemDetails.rejection_reason}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={() => setShowDetailsModal(false)}
                                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const SubmissionCard = ({ item }) => {
        const itemStatus = item.status || 'pending';
        const isPending = itemStatus === 'pending' || itemStatus === 'pending_approval';
        const isApproved = itemStatus === 'approved';
        const isRejected = itemStatus === 'rejected';

        return (
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <Receipt className="w-4 h-4 text-gray-500" />
                        <h3 className="font-medium text-gray-900">
                            {item.reference_number || `#${item.id}`}
                        </h3>
                        <span className={`px-2 py-1 text-xs rounded-full flex items-center gap-1 ${getStatusColor(itemStatus)}`}>
                            {getStatusIcon(itemStatus)}
                            {isPending ? 'Pending' : isApproved ? 'Approved' : 'Rejected'}
                        </span>
                    </div>
                    <div className="text-right">
                        <p className="font-semibold text-gray-900">{formatCurrency(item.amount)}</p>
                        <p className="text-xs text-gray-500">{formatDate(item.created_at)}</p>
                    </div>
                </div>

                <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Description:</span>
                        <span className="font-medium truncate ml-2" title={item.description}>
                            {item.description || 'No description'}
                        </span>
                    </div>

                    {item.vendor_name && (
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Vendor:</span>
                            <span className="font-medium">{item.vendor_name}</span>
                        </div>
                    )}

                    {item.customer_name && (
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Customer:</span>
                            <span className="font-medium">{item.customer_name}</span>
                        </div>
                    )}

                    <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Date:</span>
                        <span className="font-medium">{formatDate(item.date)}</span>
                    </div>

                    <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Accounting Status:</span>
                        <span className={`px-2 py-1 text-xs rounded-full ${getAccountingStatusColor(item.accounting_status)}`}>
                            {item.accounting_status}
                        </span>
                    </div>

                    {isApproved && item.approved_by && (
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Approved by:</span>
                            <span className="font-medium text-green-600">{item.approved_by}</span>
                        </div>
                    )}

                    {isRejected && item.rejection_reason && (
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Rejection Reason:</span>
                            <span className="font-medium text-red-600">{item.rejection_reason}</span>
                        </div>
                    )}
                </div>

                <div className="flex gap-2">
                    <button
                        className="flex-1 flex items-center justify-center gap-1 bg-gray-100 text-gray-600 px-3 py-2 rounded-md hover:bg-gray-200 transition-colors text-sm"
                        title="View Details"
                        onClick={() => fetchItemDetails(item)}
                        disabled={loadingDetails}
                    >
                        <Eye className="w-4 h-4" />
                        {loadingDetails ? 'Loading...' : 'View Details'}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Employee Dashboard</h1>
                        <p className="text-gray-600">View your submitted transactions and their status</p>
                        <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                            <span>Company: <strong>{currentUser.companyName}</strong></span>
                            <span>•</span>
                            <span>Access: <strong>Employee (Read Only)</strong></span>
                            <span>•</span>
                            <span>User: <strong>{currentUser.fullName}</strong></span>
                            {currentOrganization && (
                                <>
                                    <span>•</span>
                                    <span>Org: <strong>{currentOrganization.name}</strong></span>
                                </>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            fetchMySubmissions();
                            fetchEmployeeStats();
                        }}
                        className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700 transition-colors"
                        disabled={loading}
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Total Submissions</p>
                            <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
                        </div>
                        <FileText className="w-8 h-8 text-blue-500" />
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Pending</p>
                            <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                        </div>
                        <Clock className="w-8 h-8 text-yellow-500" />
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Approved</p>
                            <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
                        </div>
                        <CheckCircle className="w-8 h-8 text-green-500" />
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Rejected</p>
                            <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
                        </div>
                        <AlertCircle className="w-8 h-8 text-red-500" />
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Total Amount</p>
                            <p className="text-2xl font-bold text-purple-600">{formatCurrency(stats.totalAmount)}</p>
                        </div>
                        <DollarSign className="w-8 h-8 text-purple-500" />
                    </div>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="bg-white border border-gray-200 rounded-lg mb-6">
                <div className="border-b border-gray-200">
                    <nav className="flex overflow-x-auto">
                        {employeeTabs.map((tab) => {
                            const IconComponent = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${activeTab === tab.id
                                        ? 'border-teal-500 text-teal-600 bg-teal-50'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    <IconComponent className="w-4 h-4" />
                                    {tab.name}
                                </button>
                            );
                        })}
                    </nav>
                </div>

                <div className="p-4 bg-green-50 border-l-4 border-green-400">
                    <div className="flex items-center gap-2 text-green-800">
                        <User className="w-4 h-4" />
                        <span className="text-sm font-medium">
                            EMPLOYEE MODE: View-only access to your submitted transactions. Contact your approver for questions about status.
                        </span>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-gray-500" />
                            <span className="text-sm font-medium text-gray-700">Status Filter:</span>
                        </div>

                        <select
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                        >
                            <option value="all">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </select>
                    </div>

                    <div className="flex items-center gap-2">
                        <Search className="w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search submissions..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="border border-gray-300 rounded-md px-3 py-1 text-sm w-64"
                        />
                        <button
                            onClick={exportData}
                            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 text-sm disabled:opacity-50"
                            disabled={mySubmissions.length === 0 || loading}
                        >
                            <Download className="w-4 h-4" />
                            Export Data
                        </button>
                    </div>
                </div>

                <div className="mt-4 grid grid-cols-4 gap-4 text-sm">
                    <div className="bg-yellow-50 p-2 rounded">
                        <span className="text-yellow-600 font-medium">Pending: </span>
                        <span className="font-bold">{filteredData.filter(item => item.status === 'pending' || item.status === 'pending_approval').length}</span>
                    </div>
                    <div className="bg-green-50 p-2 rounded">
                        <span className="text-green-600 font-medium">Approved: </span>
                        <span className="font-bold">{filteredData.filter(item => item.status === 'approved').length}</span>
                    </div>
                    <div className="bg-red-50 p-2 rounded">
                        <span className="text-red-600 font-medium">Rejected: </span>
                        <span className="font-bold">{filteredData.filter(item => item.status === 'rejected').length}</span>
                    </div>
                    <div className="bg-blue-50 p-2 rounded">
                        <span className="text-blue-600 font-medium">Total: </span>
                        <span className="font-bold">{filteredData.length}</span>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="space-y-6">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <User className="w-5 h-5 text-blue-500" />
                        My {employeeTabs.find(tab => tab.id === activeTab)?.name} ({filteredData.length})
                    </h2>

                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                        </div>
                    ) : filteredData.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {filteredData.map((item) => (
                                <SubmissionCard key={`${item.type}-${item.id}`} item={item} />
                            ))}
                        </div>
                    ) : (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                            <h3 className="font-medium text-gray-900 mb-1">No submissions found</h3>
                            <p className="text-gray-600 text-sm">
                                {filter === 'all'
                                    ? `You haven't submitted any ${employeeTabs.find(tab => tab.id === activeTab)?.name.toLowerCase()} yet or none match your search.`
                                    : `No ${filter} ${employeeTabs.find(tab => tab.id === activeTab)?.name.toLowerCase()} found matching your criteria.`
                                }
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Info Panel */}
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-start gap-3">
                    <Building className="w-6 h-6 text-blue-600 mt-1" />
                    <div>
                        <h3 className="font-semibold text-blue-900 mb-2">Employee Access Information</h3>
                        <div className="text-blue-800 text-sm space-y-1">
                            <p>• You can view all your submitted transactions for <strong>{currentUser.companyName}</strong></p>
                            <p>• Transaction status updates are shown in real-time</p>
                            <p>• <strong>Accounting Status:</strong> Shows whether your submission has been synced to accounting system</p>
                            <p>• <strong>"Accounted"</strong> = Synced to Zoho Books | <strong>"Not Accounted"</strong> = Pending sync</p>
                            <p>• Access period: <strong>{currentUser.startDate ? new Date(currentUser.startDate).toLocaleDateString() : 'N/A'} to {currentUser.endDate ? new Date(currentUser.endDate).toLocaleDateString() : 'Active'}</strong></p>
                            <p>• For questions about your submissions, contact your approver or admin</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Item Details Modal */}
            <ItemDetailsModal />
        </div>
    );
};

export default EmployeeDashboard;